import type {AxiosError} from "axios";

import {axios} from "@/src/modules/core/lib";
import {capturePushSubscriptionFailed} from "@/src/modules/core/lib/glitchtip/device-capture";

import {urlBase64ToUint8Array} from "./helpers";

const isPushSupported =
  typeof window !== "undefined" &&
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window;

function getVapidPublicKey(): string | undefined {
  const key = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  return typeof key === "string" && key.trim().length > 0 ? key.trim() : undefined;
}

async function getOrCreatePushSubscription(): Promise<PushSubscription> {
  const vapidPublicKey = getVapidPublicKey();
  if (!vapidPublicKey) {
    throw new Error("VITE_VAPID_PUBLIC_KEY is not configured");
  }

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    return existing;
  }

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
  });
}

export async function registerOrSyncPushSubscription(): Promise<void> {
  if (!isPushSupported || Notification.permission !== "granted") {
    return;
  }

  const subscription = await getOrCreatePushSubscription();
  await axios({
    url: "/push-subscriptions",
    method: "post",
    data: JSON.stringify(subscription),
  });
}

export function isPushRegistrationSupported(): boolean {
  return isPushSupported;
}

export async function registerOrSyncPushSubscriptionSafe(options?: {
  captureFailures?: boolean;
  onForbiddenMessage?: (message: string) => void;
}): Promise<void> {
  try {
    await registerOrSyncPushSubscription();
  } catch (error) {
    const axiosError =
      error && typeof error === "object" && "isAxiosError" in error
        ? (error as AxiosError<{message?: string}>)
        : undefined;
    const statusCode = axiosError?.response?.status;

    if (statusCode === 403) {
      const message = axiosError?.response?.data?.message?.trim();
      if (message) {
        options?.onForbiddenMessage?.(message);
      }
      return;
    }

    if (options?.captureFailures !== false && axiosError) {
      capturePushSubscriptionFailed(axiosError);
    }
  }
}
